import { useEffect, useState } from "react";
import { Copy, Globe, LoaderCircle, Plus, RefreshCw, ShieldCheck, Trash2, X } from "lucide-react";
import { authService } from "../../services/api";
import { useNotification } from "../../components/Notifications/NotificationContext";
import Loading from "../../components/UI/Loading/Loading";
import { fmtDay } from "./DomainMigrationNotice";

/**
 * Domínios do link: os da Spectrum (prontos para usar) e o domínio próprio do
 * usuário. O próprio só ativa depois que o DNS (registro A) aponta para o
 * servidor de redirect — a tela mostra o registro e o botão "Verificar".
 * `onChange` avisa a página para recarregar as opções do link.
 */
const errorOf = (err, fallback) => err?.response?.data?.error || fallback;

function StatusBadge({ active }) {
    return active ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-semibold text-emerald-400">Ativo</span>
    ) : (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-300">Pendente</span>
    );
}

function CopyValue({ value }) {
    const { showSuccess } = useNotification();
    return (
        <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(value); showSuccess("Copiado."); }}
            className="inline-flex items-center gap-1.5 rounded-md bg-black/30 border border-white/[0.06] px-2 py-1 font-mono text-xs text-[#c4b5fd] hover:border-white/20 transition"
            title="Copiar"
        >
            <span>{value}</span>
            <Copy size={12} aria-hidden="true" />
        </button>
    );
}

const PROVIDER_TIPS = [
    { name: "Cloudflare", tip: "DNS → Registros → Adicionar registro → tipo A. Deixe a nuvem cinza (\"Somente DNS\")." },
    { name: "Registro.br", tip: "No domínio, \"DNS\" → \"Editar zona\" → Nova entrada → tipo A." },
    { name: "Hostinger", tip: "Domínios → seu domínio → DNS / Nameservers → Adicionar registro → tipo A." },
    { name: "GoDaddy", tip: "Meus produtos → DNS do domínio → Adicionar novo registro → tipo A." },
];

// Parte antes do domínio, só para o exemplo do campo Nome (link.seusite.com → link).
const hostLabel = (hostname) => {
    const labels = hostname.split(".");
    return labels.length > 2 ? labels[0] : "@";
};

function DnsInstructions({ hostname, ips }) {
    if (!ips.length) {
        return (
            <p className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                O servidor de redirect ainda não foi configurado pela Spectrum. Tente de novo mais tarde ou fale com o suporte.
            </p>
        );
    }
    const label = hostLabel(hostname);
    return (
        <div className="mt-3 rounded-lg border border-white/[0.08] bg-black/20 p-3 text-xs text-white/70">
            <ol aria-label="Passo a passo" className="space-y-3 list-none p-0 m-0">
                <li className="flex gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[rgba(139,92,246,0.25)] text-[#c4b5fd] text-[11px] font-bold grid place-items-center">1</span>
                    <span>Abra o painel de DNS de onde você comprou o domínio (Cloudflare, Registro.br, Hostinger, GoDaddy...).</span>
                </li>
                <li className="flex gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[rgba(139,92,246,0.25)] text-[#c4b5fd] text-[11px] font-bold grid place-items-center">2</span>
                    <div className="min-w-0 flex-1">
                        <div className="mb-2">Crie um registro com estes dados:</div>
                        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-center rounded-md bg-black/20 p-2.5">
                            <span className="text-white/40">Tipo</span>
                            <span className="font-mono text-white">A</span>
                            <span className="text-white/40">Nome</span>
                            <span className="flex flex-wrap items-center gap-1.5">
                                <CopyValue value={label} />
                                <span className="text-white/35">{label === "@" ? "(domínio sem subdomínio)" : `(para ${hostname})`}</span>
                            </span>
                            <span className="text-white/40">{ips.length > 1 ? "Valor (um deles)" : "Valor"}</span>
                            <span className="flex flex-wrap gap-1.5">{ips.map((ip) => <CopyValue key={ip} value={ip} />)}</span>
                            <span className="text-white/40">TTL</span>
                            <span className="text-white/60">Automático</span>
                        </div>
                    </div>
                </li>
                <li className="flex gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[rgba(139,92,246,0.25)] text-[#c4b5fd] text-[11px] font-bold grid place-items-center">3</span>
                    <span>
                        Na Cloudflare, deixe a <b className="text-white">nuvem cinza</b> ("Somente DNS"). Com a nuvem laranja a verificação e o HTTPS não funcionam.
                    </span>
                </li>
                <li className="flex gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[rgba(139,92,246,0.25)] text-[#c4b5fd] text-[11px] font-bold grid place-items-center">4</span>
                    <span>Apague outros registros A, AAAA ou CNAME com o mesmo nome, se existirem.</span>
                </li>
                <li className="flex gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[rgba(139,92,246,0.25)] text-[#c4b5fd] text-[11px] font-bold grid place-items-center">5</span>
                    <span>
                        Aguarde a propagação (normalmente minutos, às vezes algumas horas) e clique em <b className="text-white">Verificar</b>.
                        Depois de ativo, escolha o domínio em "Preparar campanha".
                    </span>
                </li>
            </ol>
            <details className="mt-3 rounded-md border border-white/[0.06] bg-black/10 px-2.5 py-2">
                <summary className="cursor-pointer text-white/60 hover:text-white">Dicas por provedor</summary>
                <ul className="mt-2 space-y-1.5 list-none p-0 m-0">
                    {PROVIDER_TIPS.map((p) => (
                        <li key={p.name}><b className="text-white/80">{p.name}:</b> {p.tip}</li>
                    ))}
                </ul>
            </details>
        </div>
    );
}

export default function DomainsModal({ open, onClose, onChange }) {
    const api = authService.redirects;
    const { showError, showSuccess } = useNotification();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [hostname, setHostname] = useState("");
    const [adding, setAdding] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        setLoading(true);
        setData(null);
        api.list_domains()
            .then(({ data: res }) => { if (alive) setData(res?.data || null); })
            .catch((err) => showError(errorOf(err, "Erro ao carregar os domínios.")))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, reloadKey]);

    if (!open) return null;

    const own = data?.own || [];
    const ips = data?.dns_target || [];
    const spectrum = (data?.available || []).filter((d) => d.kind === "spectrum");
    const canAdd = own.length < (data?.max_own || 0);

    const replaceOwn = (row) => setData((prev) => ({ ...prev, own: prev.own.map((d) => (d.id === row.id ? row : d)) }));

    const add = async (e) => {
        e.preventDefault();
        const value = hostname.trim();
        if (!value || adding) return;
        try {
            setAdding(true);
            const { data: res } = await api.add_domain(value);
            setData((prev) => ({ ...prev, own: [...prev.own, res.data] }));
            setHostname("");
            showSuccess("Domínio cadastrado. Agora crie o registro de DNS.");
            onChange?.();
        } catch (err) {
            showError(errorOf(err, "Erro ao cadastrar o domínio."));
        } finally {
            setAdding(false);
        }
    };

    const verify = async (domain) => {
        try {
            setBusyId(domain.id);
            const { data: res } = await api.verify_domain(domain.id);
            replaceOwn(res.data);
            showSuccess(`${domain.hostname} verificado! Já dá para usar nos links.`);
            onChange?.();
        } catch (err) {
            showError(errorOf(err, "Não foi possível verificar agora."));
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (domain) => {
        if (!window.confirm(`Remover ${domain.hostname}? Links já divulgados com ele param de funcionar.`)) return;
        try {
            setBusyId(domain.id);
            await api.delete_domain(domain.id);
            setData((prev) => ({ ...prev, own: prev.own.filter((d) => d.id !== domain.id) }));
            showSuccess(`${domain.hostname} removido.`);
            onChange?.();
        } catch (err) {
            showError(errorOf(err, "Erro ao remover o domínio."));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2 md:p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="domains-modal-title"
                className="relative w-full max-w-2xl rounded-2xl border border-white/[0.09] bg-[#10141a] p-4 md:p-6 shadow-[0_24px_64px_rgba(0,0,0,0.55)] text-white"
                style={{ maxHeight: "92vh", overflowY: "auto", colorScheme: "dark" }}
            >
                <div className="flex items-start justify-between mb-6 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.18)] flex items-center justify-center text-[#a78bfa]">
                            <Globe size={20} aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                            <h2 id="domains-modal-title" className="text-lg font-semibold">Domínios dos links</h2>
                            <p className="text-sm text-white/50">Escolha em qual domínio seus links aparecem.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="action-btn flex-shrink-0" aria-label="Fechar">
                        <X size={18} />
                    </button>
                </div>

                {loading ? (
                    <Loading text="Carregando domínios..." />
                ) : !data ? (
                    <div className="py-6 text-center">
                        <p className="text-sm text-white/60 mb-4">Não foi possível carregar os domínios agora.</p>
                        <button type="button" className="btn-secondary" onClick={() => setReloadKey((k) => k + 1)}>
                            Tentar de novo
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Domínios da Spectrum */}
                        <div className="text-xs uppercase tracking-wide text-white/40 mb-2">Domínios da Spectrum</div>
                        <div className="flex flex-wrap gap-2 mb-6">
                            {spectrum.length ? spectrum.map((d) => (
                                <span key={d.id} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-1.5 text-sm">
                                    <ShieldCheck size={14} className={d.expires_at ? "text-amber-300" : "text-emerald-400"} aria-hidden="true" /> {d.hostname}
                                    {d.expires_at && (
                                        <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-300">
                                            Expira em {fmtDay(d.expires_at)}
                                        </span>
                                    )}
                                </span>
                            )) : <span className="text-sm text-white/40">Nenhum no momento.</span>}
                        </div>

                        {/* Domínio próprio */}
                        <div className="text-xs uppercase tracking-wide text-white/40 mb-2">Seu domínio</div>
                        <p className="text-sm text-white/50 mb-3">
                            Use um domínio seu (ex.: <code className="text-white/70">link.seusite.com</code>) nos links. Ele vale só para os seus redirecionadores.
                        </p>

                        <div className="space-y-3">
                            {own.map((d) => {
                                const active = d.status === "active";
                                const busy = busyId === d.id;
                                return (
                                    <div key={d.id} data-testid={`own-domain-${d.id}`}
                                        className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
                                        <div className="flex items-center justify-between gap-3 flex-wrap">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="font-semibold truncate">{d.hostname}</span>
                                                <StatusBadge active={active} />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {!active && (
                                                    <button type="button" className="btn-primary text-sm inline-flex items-center gap-1.5"
                                                        disabled={busy} onClick={() => verify(d)}>
                                                        {busy ? <LoaderCircle size={14} className="animate-spin" /> : <RefreshCw size={14} />} Verificar
                                                    </button>
                                                )}
                                                <button type="button" className="action-btn" disabled={busy}
                                                    aria-label={`Remover ${d.hostname}`} title="Remover" onClick={() => remove(d)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                        {!active && <DnsInstructions hostname={d.hostname} ips={ips} />}
                                    </div>
                                );
                            })}
                        </div>

                        {canAdd && (
                            <form onSubmit={add} className={`flex flex-col sm:flex-row gap-2 ${own.length ? "mt-4" : ""}`}>
                                <label htmlFor="own-domain-input" className="sr-only">Seu domínio</label>
                                <input
                                    id="own-domain-input"
                                    type="text"
                                    value={hostname}
                                    onChange={(e) => setHostname(e.target.value)}
                                    placeholder="link.seusite.com"
                                    autoComplete="off"
                                    spellCheck={false}
                                    maxLength={253}
                                    className="flex-1 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-white/40 outline-none focus:border-[rgba(139,92,246,0.4)]"
                                />
                                <button type="submit" className="btn-primary inline-flex items-center justify-center gap-1.5" disabled={adding}>
                                    {adding ? <LoaderCircle size={15} className="animate-spin" /> : <Plus size={15} />} Adicionar
                                </button>
                            </form>
                        )}

                        <div className="flex justify-end mt-6">
                            <button onClick={onClose} className="btn-secondary">Fechar</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
